import type { ComponentType } from 'react';

import { ApprovalRequestState } from '../../../../../../types/ApprovalRequestState';
import { Text } from '../../../../../cdl/Text/Text';
import { translate } from '../../../../../common/helpers/translate.helper';
import { IconCircleCheck } from '../../../../../common/icons/cdl/CircleCheck';
import { IconCircleDotted } from '../../../../../common/icons/cdl/CircleDotted';
import { IconCircleX } from '../../../../../common/icons/cdl/CircleX';
import type { OfferModel } from '../../../../../offer/model/OfferModel';
import { OfferState } from '../../../../../offer/model/OfferState';
import { MetaInformation } from '../../../common/OfferCard/MetaInformation';
import type { MetaInformationProps } from '../../../common/OfferCard/MetaInformation';
import { useOfferCardContext } from '../hooks/useOfferCardContext';

interface OfferStatusContent {
    Icon: ComponentType<{ width?: number; height?: number }>;
    variant: MetaInformationProps['variant'];
    text: string;
}

const getApprovalRequestStatus = (state: ApprovalRequestState): OfferStatusContent | null => {
    if (state === ApprovalRequestState.PENDING) {
        return {
            Icon: IconCircleDotted,
            variant: 'neutral',
            text: translate('order.detail.offerCard.waitingForApproval'),
        };
    }

    if (state === ApprovalRequestState.DECLINED) {
        return { Icon: IconCircleX, variant: 'negative', text: translate('order.detail.offerCard.declined') };
    }

    if (state === ApprovalRequestState.APPROVED) {
        return { Icon: IconCircleCheck, variant: 'positive', text: translate('order.detail.offerCard.approved') };
    }

    if (state === ApprovalRequestState.WITHDRAWN) {
        return { Icon: IconCircleDotted, variant: 'attention', text: translate('order.detail.offerCard.withdrawn') };
    }

    if (state === ApprovalRequestState.EXPIRED) {
        return {
            Icon: IconCircleDotted,
            variant: 'attention',
            text: translate('order.detail.offerCard.requestExpired'),
        };
    }

    return null;
};

const getOfferStatus = (offer: OfferModel): OfferStatusContent | null => {
    if (offer.wasQuoted() && offer.isUnquoted() && !offer.isCanceled()) {
        return {
            Icon: IconCircleDotted,
            variant: 'neutral',
            text: translate('order.detail.offerCard.waitingForQuoteUpdate'),
        };
    }

    if (offer.isUnquoted() && !offer.isCanceled() && !offer.approvalRequests.length) {
        return {
            Icon: IconCircleDotted,
            variant: 'neutral',
            text: translate('order.detail.offerCard.waitingForQuote'),
        };
    }

    if (offer.approvalRequests.length && !offer.isOrder() && !offer.isCanceled()) {
        return getApprovalRequestStatus(offer.approvalRequests[0].state);
    }

    if (offer.isQuotedEnquiry() && !offer.isCanceled()) {
        return { Icon: IconCircleCheck, variant: 'neutral', text: translate('order.detail.offerCard.quoted') };
    }

    if (offer.isCanceled()) {
        return {
            Icon: IconCircleX,
            variant: 'negative',
            text:
                offer.state === OfferState.ENQUIRY_DECLINED || offer.state === OfferState.QUOTE_DECLINED
                    ? translate('order.detail.offerCard.enquiryDeclined')
                    : translate('order.detail.offerCard.canceled'),
        };
    }

    return { Icon: IconCircleCheck, variant: 'positive', text: translate('order.detail.offerCard.ordered') };
};

export const OfferStatus = () => {
    const { offer } = useOfferCardContext();
    const status = getOfferStatus(offer);

    if (!status) {
        return null;
    }

    const { Icon, variant, text } = status;

    return (
        <MetaInformation variant={variant}>
            <Icon width={16} height={16} />
            <Text variant="extraSmall" fontWeight={600}>
                {text}
            </Text>
        </MetaInformation>
    );
};
